import { effect } from 'kerfjs';

import { editorSegment, lineRowWindow, view, workspace } from '../data/state.js';

/**
 * Progressive Line items rows (HS2-HVR0HY). Building every row of a 300-line demand in one
 * task cost one ~190 ms long task, mostly style and layout for the fresh rows. A new mount of
 * the list now renders the first screenful, then adds the rest in idle-time chunks, so each
 * task stays short. Code that needs a row that may not exist yet calls `showAllLineRows()`; focus
 * entering a row (keyboard row navigation) and the browser's find shortcut do so as well.
 */

/** Rows the first render builds: more than a tall screen shows, so an early scroll finds rows. */
export const LINE_ROWS_FIRST = 40;
/** Rows each idle chunk adds; retuned for Kerf beta 93, see docs/7-architecture.md (HS2-HRMAXH). */
export const LINE_ROWS_STEP = 90;

/** Names one mount of the Line items list; leaving the view, demand or segment ends it. */
export function lineRowsKey(demandId: string): string {
  return `${view.value}|${demandId}|${editorSegment.value}`;
}

/** How many rows the list mounted under `key` renders now. */
export function lineRowLimit(key: string): number {
  const current = lineRowWindow.value;
  return current.key === key ? current.count : LINE_ROWS_FIRST;
}

/** Render every row of the current list now, e.g. before scrolling to a referenced line. */
export function showAllLineRows(): void {
  const current = lineRowWindow.value;
  if (current.count !== Number.POSITIVE_INFINITY) lineRowWindow.value = { ...current, count: Number.POSITIVE_INFINITY };
}

type Idle = (callback: () => void) => void;

const defaultIdle: Idle = (callback) => {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(callback, { timeout: 200 });
  else setTimeout(callback, 16);
};

/** Keep the row window keyed to the current mount and grow it while rows remain; returns a disposer. */
export function wireProgressiveLineRows(root: HTMLElement, idle: Idle = defaultIdle): () => void {
  // The chunk already queued for this mount and count; a stale chunk never blocks a new mount.
  let pendingFor = '';
  // Row navigation (Home/End, arrows) and find-in-page need every row in the DOM.
  const onFocus = (event: FocusEvent): void => {
    if (event.target instanceof Element && event.target.closest('[data-line-item]') !== null) showAllLineRows();
  };
  const onKey = (event: KeyboardEvent): void => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f') showAllLineRows();
  };
  root.addEventListener('focusin', onFocus);
  document.addEventListener('keydown', onKey, { capture: true });
  const dispose = effect(() => {
    const ws = workspace.value;
    const demandId = (ws?.demand as { id?: string } | undefined)?.id ?? '';
    const key = lineRowsKey(demandId);
    const current = lineRowWindow.value;
    if (current.key !== key) {
      // A new mount starts from the first screenful; this write re-runs the effect.
      lineRowWindow.value = { key, count: LINE_ROWS_FIRST };
      return;
    }
    const total = ((ws?.lineItems ?? []) as unknown[]).length;
    if (view.value !== 'workspace' || editorSegment.value !== 'lines' || current.count >= total) return;
    const tag = `${key}#${String(current.count)}`;
    if (pendingFor === tag) return;
    pendingFor = tag;
    idle(() => {
      if (pendingFor === tag) pendingFor = '';
      const latest = lineRowWindow.value;
      // The list was remounted or filled meanwhile; the effect has already queued what it needs.
      if (latest.key !== key || latest.count !== current.count) return;
      lineRowWindow.value = { key, count: latest.count + LINE_ROWS_STEP };
    });
  });
  return () => {
    dispose();
    root.removeEventListener('focusin', onFocus);
    document.removeEventListener('keydown', onKey, { capture: true });
  };
}
